import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useEffect, useRef, useState } from 'react'
import { useDebounced } from 'src/hooks'
import { useNormalizeBackendError } from 'src/utils/error.utils'

export interface UseAsyncTextInputOptions {
  /**
   * The current value from external state (controlled value)
   */
  value?: string
  /**
   * callback that handles the async operation when the input value changes
   */
  onValueChange: (value: string) => Promise<void> | void
  /**
   * Debounce delay in milliseconds
   * @default 500
   */
  debounceDelay?: number
  /**
   * Save the current value when the input loses focus instead of while typing.
   * @default false
   */
  saveOnBlur?: boolean
  /**
   * Identifies the resource being edited. Changing it discards any local draft
   * for the previous resource, even if both resources currently have the same
   * controlled value.
   */
  resetKey?: string
}

export interface UseAsyncTextInputReturn {
  /**
   * local value to reflect user changes
   */
  value: string
  loadingState: LoadingState
  errorText: string | undefined
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
}

/**
 * Hook that manages async text input with debouncing, loading states, and error handling.
 * Abstracts the common pattern of local state + debounced async operations.
 */
export const useAsyncTextInput = ({
  value = '',
  onValueChange,
  debounceDelay = 500,
  saveOnBlur = false,
  resetKey,
}: UseAsyncTextInputOptions): UseAsyncTextInputReturn => {
  const normalizeError = useNormalizeBackendError()

  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState<string>(value)
  const [errorText, setErrorText] = useState<string | undefined>()
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')

  // Tracks the last value the server has confirmed (or the initial controlled
  // value). Used both to distinguish a server-echoed `value` prop update
  // (which should NOT clobber in-flight user edits) from a genuinely external
  // reset (which should), and as the rollback target on a failed commit.
  const lastCommittedValueRef = useRef<string>(value)
  const localValueRef = useRef<string>(value)
  const pendingBlurSaveCountRef = useRef(0)
  const lastQueuedBlurValueRef = useRef<string | undefined>()
  const blurSaveQueueRef = useRef<Promise<void>>(Promise.resolve())
  const queueBlurSaveRef = useRef<(force?: boolean) => void>(() => undefined)
  const cancelDebouncedSaveRef = useRef<() => void>(() => undefined)
  const resetKeyRef = useRef<string | undefined>(resetKey)
  const resourceGenerationRef = useRef(0)

  // Handle async value change with error handling
  const handleAsyncValueChange = async (
    newValue: string,
    resourceGeneration = resourceGenerationRef.current,
  ) => {
    const result = onValueChange(newValue)

    // Check if onValueChange returns a Promise
    if (
      result &&
      typeof result === 'object' &&
      typeof result.then === 'function'
    ) {
      setLoadingState('pending')

      // TODO: consider abstracting away disabled behaviour in the hook as well
      await result
        .then(() => {
          if (resourceGeneration !== resourceGenerationRef.current) return
          lastCommittedValueRef.current = newValue
          setLoadingState('success')
          setErrorText(undefined)
        })
        .catch((error) => {
          if (resourceGeneration !== resourceGenerationRef.current) return
          const e = normalizeError(error)
          setLoadingState('error')
          setErrorText(e?.error)
          // Roll back to the last value the server has confirmed — but only
          // if the textbox still holds the value that just failed. If the
          // user has typed past it, leave their newer input alone.
          if (!saveOnBlur) {
            setLocalValue((current) =>
              current === newValue ? lastCommittedValueRef.current : current,
            )
          }
        })
    } else {
      if (resourceGeneration !== resourceGenerationRef.current) return
      lastCommittedValueRef.current = newValue
    }
  }

  // Debounce the async handler
  const {
    debouncedF: debouncedHandleAsyncValueChange,
    cancel: cancelDebouncedHandleAsyncValueChange,
  } = useDebounced(handleAsyncValueChange, debounceDelay)
  cancelDebouncedSaveRef.current = cancelDebouncedHandleAsyncValueChange

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value

    // Clear any previous error — both the message and the error indicator —
    // so the user isn't typing a correction with the red error UI still on.
    // Preserve 'pending' and 'success' across keystrokes so the indicator
    // does not flicker.
    if (errorText !== undefined) {
      setErrorText(undefined)
    }
    if (loadingState === 'error') {
      setLoadingState('idle')
    }

    // Update local state immediately for responsive UI
    localValueRef.current = newValue
    setLocalValue(newValue)

    // Save-on-blur callers keep their edits local while the user types.
    if (!saveOnBlur) {
      debouncedHandleAsyncValueChange(newValue)
    }
  }

  const queueBlurSave = (force = false) => {
    if (!saveOnBlur && !force) return

    const valueToSave = localValueRef.current
    const hasPendingBlurSave = pendingBlurSaveCountRef.current > 0
    const resourceGeneration = resourceGenerationRef.current

    // Avoid duplicate blur events, while still allowing a user to revert to a
    // value that matches the last committed value while another write is queued.
    if (
      (hasPendingBlurSave && valueToSave === lastQueuedBlurValueRef.current) ||
      (!hasPendingBlurSave && valueToSave === lastCommittedValueRef.current)
    ) {
      return
    }

    // Keep writes ordered. If a user types and blurs again while the first
    // request is still in flight, the newer value is sent only afterwards.
    pendingBlurSaveCountRef.current += 1
    lastQueuedBlurValueRef.current = valueToSave
    const queuedSave = blurSaveQueueRef.current
      .catch(() => undefined)
      .then(() => {
        if (resourceGeneration !== resourceGenerationRef.current) return
        return handleAsyncValueChange(valueToSave, resourceGeneration)
      })
    const settledSave = queuedSave.finally(() => {
      if (resourceGeneration !== resourceGenerationRef.current) return
      pendingBlurSaveCountRef.current -= 1
      if (pendingBlurSaveCountRef.current === 0) {
        lastQueuedBlurValueRef.current = undefined
      }
    })

    // A synchronous callback failure must not block a later blur save.
    blurSaveQueueRef.current = settledSave.catch(() => undefined)
  }

  queueBlurSaveRef.current = queueBlurSave

  // Clicking away can unmount the field before React delivers its blur event
  // (for example, when switching transaction-detail tabs). Commit the final
  // local value during unmount; a preceding blur is deduped above.
  useEffect(() => {
    return () => {
      if (saveOnBlur) {
        queueBlurSaveRef.current(true)
      }
    }
  }, [saveOnBlur])

  // Mode changes are possible through the Feature Flagger while a field is
  // active. A pending debounce must not escape into blur-save mode; the
  // cleanup above commits a blur draft before switching back to timer saves.
  useEffect(() => {
    if (saveOnBlur) {
      cancelDebouncedSaveRef.current()
    }
  }, [saveOnBlur])

  const handleBlur = (
    _e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    queueBlurSave()
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // A modal focus trap can keep Tab on the textarea, so no native blur is
    // emitted. Queue the same save here; an ensuing native blur is deduped.
    if (e.key === 'Tab') {
      queueBlurSave()
    }
  }

  // A transaction-detail form is reused when the selected transaction changes.
  // Reset local draft, queue, and UI state by identity so a blank value on the
  // next transaction cannot leave the previous transaction's draft in place.
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return

    resetKeyRef.current = resetKey
    resourceGenerationRef.current += 1
    lastCommittedValueRef.current = value
    localValueRef.current = value
    pendingBlurSaveCountRef.current = 0
    lastQueuedBlurValueRef.current = undefined
    blurSaveQueueRef.current = Promise.resolve()
    setLocalValue(value)
    setErrorText(undefined)
    setLoadingState('idle')
  }, [resetKey, value])

  // Sync local value with external value changes — but only when the change is
  // genuinely external. If the new `value` matches what we last committed, it
  // is the server echoing our own write back at us, and resetting local state
  // would erase any characters the user has typed since the commit.
  useEffect(() => {
    if (value === lastCommittedValueRef.current) return
    lastCommittedValueRef.current = value
    localValueRef.current = value
    setLocalValue(value)
  }, [value])

  return {
    value: localValue,
    loadingState,
    errorText,
    onChange: handleInputChange,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
  }
}
